/**
 * Keeps the view state of a single <FolderTree>
 */
import { action, observable, computed } from "mobx";
import { RefObject } from "react";

type FolderTreeId = string;

enum FolderAnimationStates {
  Visible = "visible",
  Hidden = "hidden",
}

interface IFolderViewData {
  id: FolderTreeId;
  parent?: FolderTreeId;
  name: string;

  // Should this folder be in 'rename mode' with an input box?
  renaming?: boolean;

  // Animation state
  animationState?: FolderAnimationStates;

  // Loading state
  isLoading?: boolean;

  // Ref to the parent for measuring the transition offset
  treeParentRef?: RefObject<HTMLElement>;

  disableSelect?: boolean;
}

interface IFolderViewState {
  isToggledOpen: boolean;
}

interface ISelectionStateProvider {
  selectedFolder?: FolderTreeId;
}

class FolderTreeViewStore {
  @observable
  private _treeState: Map<FolderTreeId, IFolderViewState> = new Map<FolderTreeId, IFolderViewState>();

  /**
   * @param getFoldersFunc A getter function that returns a list of folders for this folder tree.
   * The expected use case is for the provided function to return a list of folders from a store
   * (or a list of folders @computed from a store)
   *
   * @param selectionStateProvider An external source for the selection state. Note how this store
   * only *reads* the selection state.
   */
  constructor(
    private getFoldersFunc: () => IFolderViewData[],
    private selectionStateProvider: ISelectionStateProvider,
  ) {}

  /**
   * Get the list of folders applicable to this Folder view
   */
  public get folders(): IFolderViewData[] {
    return this.getFoldersFunc();
  }

  public isFolderToggledOpen(id: FolderTreeId): boolean {
    const folderState: IFolderViewState | undefined = this._treeState.get(id);

    if (!!folderState) {
      return folderState.isToggledOpen;
    } else {
      return false;
    }
  }

  public isFolderSelected(id: FolderTreeId): boolean {
    return id === this.selectedFolder;
  }

  @action
  public onToggle(folderId: FolderTreeId): void {
    const folderState = this.getOrCreateViewState(folderId);
    folderState.isToggledOpen = !folderState.isToggledOpen;
  }

  @computed
  private get selectedFolder(): FolderTreeId | undefined {
    return this.selectionStateProvider.selectedFolder;
  }

  @action
  private getOrCreateViewState(folderId: FolderTreeId): IFolderViewState {
    if (!this._treeState.has(folderId)) {
      this._treeState.set(folderId, {
        isToggledOpen: false,
      });
    }

    // We assume here that _treeState has view state for the folderId because of the earlier check
    return this._treeState.get(folderId) as IFolderViewState;
  }
}

export { IFolderViewData, ISelectionStateProvider, FolderTreeViewStore, FolderTreeId, FolderAnimationStates };
